<template>
  <div class="Login min-h-screen flex flex-col lg:flex-row-reverse">
    <!--//v login form container  -->
    <transition name="auth-form" appear>
      <div
        class="Login-form p-4 flex-1 flex flex-col md:p-8 lg:px-20 justify-center lg:max-h-screen lg:overflow-y-auto"
      >
        <div>
          <h1 class="font-minion text-xl text-primary-400 lg:text-3xl">
            Sign in to
            <span class="block font-minion font-bold mt-0.5 lg:mt-2"
              >Ask A Fundraiser</span
            >
          </h1>
          <div v-if="isConfirmed" class="centered-container">
            <div class="card">
              <div class="circle">
                <i class="checkmark">✓</i>
              </div>
              <h1>Success</h1>
              <p>Thank you for confirming your account</p>
              <br />
              <p>you can login now!</p>
            </div>
          </div>

          <!-- //v form -->
          <form
            class="mt-5 flex-1 flex flex-col"
            @submit.prevent="onSubmitForm"
          >
            <div class="flex-1">
              <div class="">
                <LoginFormInput
                  input-label="Email"
                  label-for="login-email-input"
                  input-type="text"
                  input-id="login-email-input"
                  input-placeholder="e.g. name@domain.com"
                  v-model.trim="formData.emailVal"
                  :inputClasses="`${
                    v$.emailVal.$error || loginStatus.isLoginFailed
                      ? 'shakeX border-red-400 !outline-red-400 duration-300 !text-red-400 placeholder:!text-red-300'
                      : ''
                  }`"
                  @form-input-change="onEmailChange"
                />
                <transition name="auth-form">
                  <p
                    class="pl-2 mt-1 text-sm text-red-400"
                    v-if="v$.emailVal.$error"
                  >
                    {{ v$.emailVal.$errors[0].$message }}
                  </p>
                </transition>
              </div>
              <div class="mt-4">
                <LoginFormInput
                  input-label="Password"
                  label-for="login-password-input"
                  input-type="password"
                  :is-password="true"
                  input-id="login-password-input"
                  input-placeholder="Type your password"
                  v-model.trim="formData.passwordVal"
                  :inputClasses="`${
                    v$.passwordVal.$error || loginStatus.isLoginFailed
                      ? 'shakeX border-red-400 !outline-red-400 duration-300 !text-red-400 placeholder:!text-red-300'
                      : ''
                  }`"
                  @form-input-change="onPasswordChange"
                />
                <transition name="auth-form">
                  <p
                    class="pl-2 mt-1 text-sm text-red-400 absolute"
                    v-if="v$.passwordVal.$error"
                  >
                    {{ v$.passwordVal.$errors[0].$message }}
                  </p>
                </transition>
              </div>
              <!--//v forget password link -->
              <div class="mt-2 flex justify-end">
                <router-link
                  class="text-placeholder text-lg font-minion"
                  to="/forgot-password"
                  >Forgot password?</router-link
                >
              </div>
            </div>
            <!--//v form submit button -->
            <transition name="auth-form">
              <p
                class="pl-2 mt-3 text-sm text-red-400 text-center"
                v-if="loginStatus.isLoginFailed"
              >
                {{ loginStatus.loginErrMsg }}
              </p>
            </transition>

            <button
              ref="submitBtn"
              class="w-full bg-[#00423D] font-minion text-[20px] text-white py-3 rounded-[8px] mt-6 font-bold lg:py-4 flex items-center justify-center"
              type="submit"
            >
              <transition name="button-switch" mode="out-in">
                <span v-if="!isSendingRequest">Sign in</span>
                <div
                  v-else
                  class="max-h-6 overflow-hidden inline-flex items-center justify-center pt-2"
                >
                  <Vue3Lottie
                    :animationData="buttonAnimation"
                    :height="120"
                    :width="120"
                  />
                </div>
              </transition>
            </button>
          </form>

          <p
            class="text-center mt-7 text-placeholder text-lg font-semibold font-minion"
          >
            Don't have an account?
            <router-link
              class="font-minion font-medium text-[#00423D] "
              to="/sign-up"
            >
              Create an Account
            </router-link>
          </p>
        </div>
      </div>
    </transition>
    <AuthSlider />
  </div>
</template>
  <script setup>
//v Vue Imports
import { ref, onMounted, reactive, onUnmounted, onBeforeMount } from "vue";

//v Pinia Store
import { useAuthStore } from "@/stores/auth";
const authStore = useAuthStore();

//v Vue Router
import { RouterLink, RouterView, useRouter } from "vue-router";
const router = useRouter();

//v Components
import AuthSlider from "@/components/AuthSlider.vue";
import LoginFormInput from "@/components/LoginFormInput.vue";
//v Assets
import ButtonAni from "@/assets/loading-button-simple.json";

//v Vue3Lottie Animation Library
import { Vue3Lottie } from "vue3-lottie";
//   import 'vue3-lottie/dist/style.css'

//v Vuelidate validation Library
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, email, minLength } from "@vuelidate/validators";

//v First of all
//v Check if user is logged in and navigate user to home
if (authStore.user) {
  // Check if there's a stored redirect URL
  const storedRedirect = localStorage.getItem('redirectAfterLogin');
  if (storedRedirect) {
    localStorage.removeItem('redirectAfterLogin');
    router.push(storedRedirect);
  } else {
    router.push({ name: "getting-started-welcome" });
  }
}

//v View State
const buttonAnimation = ref(ButtonAni);
const isSendingRequest = ref(false);
const formData = reactive({
  emailVal: "",
  passwordVal: "",
});
const loginStatus = reactive({
  isLoginFailed: false,
  loginErrMsg: "",
});

// v confirmed
const isConfirmed = ref(false);
let timeoutId = null;

//v DOM Refs
const submitBtn = ref(null);

//v From Validation Rules
const rules = {
  emailVal: {
    email: helpers.withMessage("Invalid Email Address!", email),
    required: helpers.withMessage("Field Is Required!", required),
  },
  passwordVal: {
    required: helpers.withMessage("Field Is Required!", required),
    minLengthPassword: helpers.withMessage(
      "Password should be at least 8 characters long!",
      minLength(8)
    ),
  },
};
//v useVuelidate state
const v$ = useVuelidate(rules, formData);

//v On form input change handlers
function onEmailChange(e) {
  loginStatus.isLoginFailed = false;
  loginStatus.loginErrMsg = "";
}

function onPasswordChange(e) {
  loginStatus.isLoginFailed = false;
  loginStatus.loginErrMsg = "";
}

//v Sign up Submit Function
async function onSubmitForm(e) {
  //v validate inputs
  const validationResult = await v$.value.$validate();
  if (validationResult) {
    //v Form is Valid
    isSendingRequest.value = true;
    submitBtn.value.disabled = true;
    //v Run store login action
    try {
      const loginResult = await authStore.login({
        email: formData.emailVal.toLocaleLowerCase(),
        password: formData.passwordVal,
      });

      // Check if we need to redirect to payment page
      if (loginResult.shouldRedirectToPayment) {
        router.push({ name: "payment" });
        return;
      }

      // Check if we need to redirect to onboarding
      if (loginResult.shouldRedirectToOnboarding) {
        // Extract the route name from the first incomplete step
        // e.g. '/onboarding/welcome' -> 'welcome'
        const firstIncompleteRoute = loginResult.firstIncompleteStep.split('/').pop();
        router.push({ name: firstIncompleteRoute });
      } else {
        // Check if there's a stored redirect URL (from accessing protected route while not logged in)
        const storedRedirect = localStorage.getItem('redirectAfterLogin');
        if (storedRedirect) {
          // Clear the stored redirect
          localStorage.removeItem('redirectAfterLogin');
          // Navigate to the stored URL (includes path, query, and hash)
          router.push(storedRedirect);
        } else {
          // All onboarding steps are completed, redirect to steps
          router.push({ name: "getting-started-welcome" });
        }
      }
    } catch (err) {
      loginStatus.isLoginFailed = true;
      if (err.message.includes("Network") || err.message.includes("Failed to fetch")) {
        loginStatus.loginErrMsg = "Unable to connect to the server. Please check your internet connection and try again.";
      } else if (err.message.includes("not found") || err.message.includes("404")) {
        loginStatus.loginErrMsg = "This email address is not registered. Please check your email or sign up for a new account.";
      } else if (err.message.includes("password") || err.message.includes("401")) {
        loginStatus.loginErrMsg = "Incorrect email or password. Please try again.";
      } else if (err.message.includes("locked")) {
        loginStatus.loginErrMsg = "Your account has been temporarily locked for security reasons. Please contact support to unlock it.";
      } else if (err.message.includes("too many")) {
        loginStatus.loginErrMsg = "Too many failed login attempts. Please wait a few minutes before trying again.";
      } else {
        loginStatus.loginErrMsg = "Login failed. Please check your credentials and try again.";
      }
    }

    isSendingRequest.value = false;
    submitBtn.value.disabled = false;
  } else {
    //v Form is invalid
  }
}

onMounted(() => {
  // Check if the path has "confirmed" set to true
  // console.log("hi");
  if (router.currentRoute.value.query.confirmed == "true") {
    isConfirmed.value = true;

    // Automatically hide the success message after 5 seconds
    setTimeout(() => {
      isConfirmed.value = false;
    }, 5000);
  }
});

onUnmounted(() => {
  // Clear the setTimeout when the component is unmounted
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
});
</script>
  
  <style scoped>
.centered-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
}

.card {
  background: #f1f1f1;
  padding: 30px;
  border-radius: 4px;
  box-shadow: 0 2px 3px #c8d0d8;
  text-align: center;
  max-width: 400px;
}

.circle {
  border-radius: 50%;
  height: 150px;
  width: 150px;
  background: #f8faf5;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.checkmark {
  color: #009c87;
  font-size: 80px;
}

.centered-container h1 {
  color: #009c87;
  font-family: "Nunito Sans", "Helvetica Neue", sans-serif;
  font-weight: 900;
  font-size: 32px;
  margin-bottom: 10px;
}

.centered-container p {
  color: #404f5e;
  font-family: "Nunito Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  margin: 0;
}
</style>
  